/**
 * The phone view's three big buttons (scope §5.11): Log time, Next move, Needs on-site, plus the photo button.
 * Every action is one or two taps; there are no key chords and no composer here. Owner: web-phone-pwa.
 */
import { Button, Checkbox, Dialog, Input, parseDuration, Textarea, toast } from '@patchbay/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Camera, Clock, Navigation, PackageCheck, Wrench } from 'lucide-react'
import * as React from 'react'
import { LogTimeProjectDescription } from '../projects/project-mark'
import { settlePending, useTrackedDefault } from '../time/tracked'
import { trackedKeys } from '../time/tracked-api'
import {
  assignHardwareUnit,
  caseHardwareQuery,
  describeError,
  logTime,
  type MeOut,
  type NextMoveIn,
  type OwnerKind,
  openWorkOrdersQuery,
  type PartsSwappedIn,
  photoWorkOrdersQuery,
  postPhotoNote,
  savePartsSwapped,
  setNextMove,
  type TicketChip,
  type TimeDomain,
  type TimeDomainDefault,
  type TimeEntryOut,
  type TimeSource,
  timePromptQuery,
  uploadWorkOrderPhoto,
  uuid,
} from './api'

const DOMAIN_LABEL: Record<TimeDomain, string> = { phone: 'Phone', it: 'IT', macd: 'MACD' }

function rateText(rate: string | number | null | undefined): string | null {
  if (rate === null || rate === undefined || rate === '') return null
  const n = Number(rate)
  return Number.isFinite(n) ? `$${Number.isInteger(n) ? n : n.toFixed(2)}/h` : null
}

import { nextWorkingDay, todayAt } from './my-day-data'

export interface ActionTarget {
  caseId: string
  number: string
  title: string
  ticket: TicketChip | null
}

/** Phone bottom sheet: the kit's Dialog pinned to the bottom edge, full width. */
export function Sheet({
  open,
  onOpenChange,
  title,
  description,
  children,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: React.ReactNode
  description?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      className="top-auto bottom-0 left-0 w-full max-w-none translate-x-0 rounded-b-none pb-[max(1.25rem,env(safe-area-inset-bottom))] sm:left-1/2 sm:max-w-md sm:-translate-x-1/2"
    >
      {children}
    </Dialog>
  )
}

const BIG = 'h-14 text-base'

function writeBlockReason(writeBlock: string | null): string | undefined {
  if (!writeBlock) return undefined
  return writeBlock.startsWith('Offline:') ? 'Reconnect to make this change' : 'Ask a Desk writer to make this change'
}

function useInvalidateCase() {
  const qc = useQueryClient()
  return (caseId: string) => {
    void qc.invalidateQueries({ queryKey: ['case', caseId] })
    void qc.invalidateQueries({ queryKey: ['my-day'] })
  }
}

export function timeToast(entry: TimeEntryOut, number: string) {
  const what = entry.is_no_time ? 'No time recorded' : `Logged ${entry.minutes} min`
  if (entry.desk_sync_state === 'pending_token') {
    toast.warning(`${what} on ${number}`, { description: 'Saved in Patchbay. It reaches Desk when you reconnect Desk.' })
  } else if (entry.desk_sync_state === 'failed') {
    toast.warning(`${what} on ${number}, but the Desk write failed`, { description: 'It is listed under My entries to retry.' })
  } else {
    toast.success(`${what} on ${number}`)
  }
}

const PRESETS = [15, 30, 45, 60, 90, 120]

/**
 * Log time in two taps: the bar's button opens this sheet, a preset saves. "Other" and "No time" are one step
 * further. `promptRef` is the idempotency key (a fresh one per opening, or the send's time prompt).
 */
export function LogTimeSheet({
  open,
  onOpenChange,
  target,
  source = 'mobile',
  promptRef,
  defaultMinutes,
  defaultSource,
  defaultBillable,
  billableBasis,
  domains: givenDomains,
  defaultDomain,
  writeBlock,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  target: ActionTarget
  source?: TimeSource
  promptRef?: string
  defaultMinutes?: number | null
  defaultSource?: string | null
  defaultBillable?: boolean | null
  billableBasis?: string | null
  /** Round 13: Phone / IT / MACD with each one's default (the send prompt's); otherwise read for the case. */
  domains?: TimeDomainDefault[] | null
  defaultDomain?: TimeDomain | null
  writeBlock: string | null
}) {
  const invalidate = useInvalidateCase()
  const qc = useQueryClient()
  // #186: the case's tracked (unlogged) time is the default, with its source.
  const tracked = useTrackedDefault(open ? target.caseId : null)
  const presetMinutes = tracked.minutes > 0 ? tracked.minutes : defaultMinutes
  const presetSource = tracked.minutes > 0 ? tracked.source : defaultSource
  const fetched = useQuery({ ...timePromptQuery(target.caseId), enabled: open && !givenDomains?.length, retry: false })
  const domains = givenDomains?.length ? givenDomains : fetched.data?.data.domains
  const pre = (givenDomains?.length ? defaultDomain : fetched.data?.data.domain) ?? domains?.[0]?.domain
  const [domain, setDomain] = React.useState<TimeDomain | undefined>(pre ?? undefined)
  const option = domains?.find((d) => d.domain === domain)
  const [ref, setRef] = React.useState(() => promptRef ?? `mobile:${uuid()}`)
  const [mode, setMode] = React.useState<'presets' | 'other' | 'no-time'>('presets')
  const [other, setOther] = React.useState(presetMinutes ? String(presetMinutes) : '')
  const [note, setNote] = React.useState('')
  const [reason, setReason] = React.useState('')
  const [billable, setBillable] = React.useState(defaultBillable ?? false)
  React.useEffect(() => {
    if (open) {
      setRef(promptRef ?? `mobile:${uuid()}`)
      setMode('presets')
      setReason('')
      setNote('')
      setBillable(defaultBillable ?? false)
    }
  }, [defaultBillable, open, promptRef])
  // Pre-pick once the defaults are known (they may arrive after the sheet opens).
  const prePicked = React.useRef<string | null>(null)
  React.useEffect(() => {
    if (!open) {
      prePicked.current = null
      return
    }
    const first = domains?.find((d) => d.domain === pre)
    if (!first || prePicked.current) return
    prePicked.current = first.domain
    setDomain(first.domain)
    setBillable(first.billable_default)
  }, [open, pre, domains])
  const pickDomain = (d: TimeDomainDefault) => {
    prePicked.current = d.domain
    setDomain(d.domain)
    setBillable(d.billable_default)
  }
  const basis = option ? option.basis : billableBasis
  const rate = billable ? rateText(option?.hourly_rate) : null

  const m = useMutation({
    networkMode: 'always',
    mutationFn: (body: { minutes: number; note?: string; noTime?: string }) => {
      if (!target.ticket) throw new Error('This case has no live Desk ticket to log time on.')
      return logTime({
        case_id: target.caseId,
        desk_ticket_id: target.ticket.desk_ticket_id,
        minutes: body.noTime ? 0 : body.minutes,
        billable: body.noTime ? false : billable,
        no_time: Boolean(body.noTime),
        no_time_reason: body.noTime ?? null,
        note: body.note || null,
        prompt_ref: ref,
        source,
        domain: body.noTime ? null : (domain ?? null),
        tracked_seconds: tracked.known ? tracked.seconds : null,
      })
    },
    onSuccess: (entry) => {
      timeToast(entry, target.number)
      invalidate(target.caseId)
      settlePending(target.caseId)
      void qc.invalidateQueries({ queryKey: trackedKeys.case(target.caseId) })
      void qc.invalidateQueries({ queryKey: trackedKeys.unlogged })
      onOpenChange(false)
    },
  })
  const blocked = writeBlockReason(writeBlock) ?? (target.ticket ? null : 'Link a live Desk ticket before logging time')
  const otherMinutes = parseDuration(other)
  const actionReason = blocked ?? (m.isPending ? 'Saving…' : undefined)
  const presetReasonId = React.useId()
  const otherReasonId = React.useId()
  const noTimeReasonId = React.useId()

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      title="Log time"
      description={<LogTimeProjectDescription caseId={target.caseId} number={target.number} title={target.title} active={open} />}
    >
      {blocked ? <p className="mb-3 rounded-md bg-warning-soft px-3 py-2 text-sm text-warning">{blocked}</p> : null}
      {tracked.error ? (
        <p role="status" className="mb-3 rounded-md bg-warning-soft px-3 py-2 text-sm text-warning">
          Tracked time is unreachable: {tracked.error} Enter the time yourself; zero has not been assumed.
        </p>
      ) : null}
      {m.isError ? (
        <p role="alert" className="mb-3 rounded-md bg-destructive-soft px-3 py-2 text-sm text-destructive">
          {describeError(m.error)}
        </p>
      ) : null}
      {mode !== 'no-time' ? (
        <>
          {domains?.length ? (
            <div role="radiogroup" aria-label="Work type" className="mb-2 grid grid-cols-3 gap-2">
              {domains.map((d) => (
                <Button
                  key={d.domain}
                  type="button"
                  role="radio"
                  aria-checked={d.domain === domain}
                  variant={d.domain === domain ? 'primary' : 'outline'}
                  className="h-12"
                  onClick={() => pickDomain(d)}
                >
                  {DOMAIN_LABEL[d.domain]}
                </Button>
              ))}
            </div>
          ) : null}
          <fieldset className="mb-3">
            <legend className="mb-1.5 text-sm font-medium text-muted-foreground">Billing</legend>
            <Button
              type="button"
              variant={billable ? 'primary' : 'outline'}
              className="h-12 w-full"
              aria-pressed={billable}
              onClick={() => setBillable((value) => !value)}
            >
              {billable ? `Billable time${rate ? ` · ${rate}` : ''}` : 'Not billable'}
            </Button>
            {basis ? <p className="mt-1 text-xs text-muted-foreground">{basis}</p> : null}
          </fieldset>
        </>
      ) : null}
      {mode === 'presets' ? (
        <>
          {presetMinutes ? (
            <Button
              variant="primary"
              className={`${BIG} mb-2 w-full`}
              disabled={Boolean(blocked) || m.isPending}
              disabledReason={actionReason}
              disabledReasonId={presetReasonId}
              onClick={() => m.mutate({ minutes: presetMinutes })}
            >
              Log {presetMinutes} min{presetSource ? ` (${presetSource})` : ''}
            </Button>
          ) : null}
          <div className="grid grid-cols-3 gap-2">
            {PRESETS.map((n) => (
              <Button
                key={n}
                aria-label={`Log ${n} min`}
                className={BIG}
                disabled={Boolean(blocked) || m.isPending}
                disabledReason={actionReason}
                disabledReasonId={presetReasonId}
                onClick={() => m.mutate({ minutes: n })}
              >
                {n < 60 ? `${n} min` : `${n / 60} h`}
              </Button>
            ))}
          </div>
          {actionReason ? (
            <p id={presetReasonId} className="mt-2 text-xs text-muted-foreground">
              {actionReason}
            </p>
          ) : null}
          <div className="mt-2 grid grid-cols-2 gap-2">
            <Button className={BIG} variant="ghost" onClick={() => setMode('other')}>
              Other amount
            </Button>
            <Button className={BIG} variant="ghost" onClick={() => setMode('no-time')}>
              No time
            </Button>
          </div>
        </>
      ) : mode === 'other' ? (
        <form
          className="space-y-2"
          onSubmit={(e) => {
            e.preventDefault()
            if (otherMinutes) m.mutate({ minutes: otherMinutes, note })
          }}
        >
          <Input
            aria-label="Minutes"
            inputMode="decimal"
            placeholder="45 or 1.5h"
            value={other}
            onChange={(e) => setOther(e.target.value)}
            className="h-12 text-base"
          />
          <Input
            aria-label="Time note"
            placeholder="Note (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="h-12 text-base"
          />
          <div className="grid grid-cols-2 gap-2">
            <Button className={BIG} variant="ghost" type="button" onClick={() => setMode('presets')}>
              Back
            </Button>
            <Button
              className={BIG}
              variant="primary"
              type="submit"
              disabled={!otherMinutes || Boolean(blocked) || m.isPending}
              disabledReason={!otherMinutes ? 'Enter a time amount' : actionReason}
              disabledReasonId={otherReasonId}
            >
              {otherMinutes ? `Log ${otherMinutes} min` : 'Log time'}
            </Button>
          </div>
          {!otherMinutes || actionReason ? (
            <p id={otherReasonId} className="text-xs text-muted-foreground">
              {!otherMinutes ? 'Enter a time amount' : actionReason}
            </p>
          ) : null}
        </form>
      ) : (
        <form
          className="space-y-2"
          onSubmit={(e) => {
            e.preventDefault()
            if (reason.trim()) m.mutate({ minutes: 0, noTime: reason.trim() })
          }}
        >
          <Input
            aria-label="Reason for no time"
            placeholder="Why no time? (e.g. covered by the packet)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="h-12 text-base"
          />
          <div className="grid grid-cols-2 gap-2">
            <Button className={BIG} variant="ghost" type="button" onClick={() => setMode('presets')}>
              Back
            </Button>
            <Button
              className={BIG}
              variant="primary"
              type="submit"
              disabled={!reason.trim() || Boolean(blocked) || m.isPending}
              disabledReason={!reason.trim() ? 'Write why there is no time' : actionReason}
              disabledReasonId={noTimeReasonId}
            >
              Record no time
            </Button>
          </div>
          {!reason.trim() || actionReason ? (
            <p id={noTimeReasonId} className="text-xs text-muted-foreground">
              {!reason.trim() ? 'Write why there is no time' : actionReason}
            </p>
          ) : null}
        </form>
      )}
    </Sheet>
  )
}

type DueChoice = 'today' | 'tomorrow' | 'next-week'
const DUE: { id: DueChoice; label: string; at: (now: Date) => Date }[] = [
  { id: 'today', label: 'Today 5 pm', at: (now) => todayAt(now, 17) },
  { id: 'tomorrow', label: 'Next work day 9 am', at: (now) => nextWorkingDay(now, 9) },
  {
    id: 'next-week',
    label: 'In a week',
    at: (now) => {
      const d = todayAt(now, 9)
      d.setDate(d.getDate() + 7)
      return d
    },
  },
]

const NEXT_PRESETS: { text: string; owner: OwnerKind }[] = [
  { text: 'Call customer', owner: 'us' },
  { text: 'Return visit', owner: 'us' },
  { text: 'Order parts', owner: 'us' },
  { text: 'Waiting on customer', owner: 'customer' },
]

export function NextMoveSheet({
  open,
  onOpenChange,
  target,
  me,
  writeBlock,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  target: ActionTarget
  me: MeOut
  writeBlock: string | null
}) {
  const invalidate = useInvalidateCase()
  const [due, setDue] = React.useState<DueChoice>('tomorrow')
  const [text, setText] = React.useState('')
  React.useEffect(() => {
    if (open) {
      setDue('tomorrow')
      setText('')
    }
  }, [open])
  const m = useMutation({
    networkMode: 'always',
    mutationFn: (p: { text: string; owner: OwnerKind }) => {
      const at = (DUE.find((d) => d.id === due) ?? DUE[1])?.at(new Date()) ?? new Date()
      const body: NextMoveIn = {
        text: p.text,
        owner_kind: p.owner,
        owner_user_id: p.owner === 'us' ? me.id : null,
        due_at: at.toISOString(),
      }
      return setNextMove(target.caseId, body)
    },
    onSuccess: (move) => {
      toast.success(`Next move on ${target.number}: ${move.text}`)
      invalidate(target.caseId)
      onOpenChange(false)
    },
  })
  const disabled = Boolean(writeBlock) || m.isPending
  const actionReason = writeBlockReason(writeBlock) ?? (m.isPending ? 'Saving…' : undefined)
  const presetReasonId = React.useId()
  const customReasonId = React.useId()
  return (
    <Sheet open={open} onOpenChange={onOpenChange} title="Next move" description={`${target.number} · ${target.title}`}>
      {writeBlock ? <p className="mb-3 rounded-md bg-warning-soft px-3 py-2 text-sm text-warning">{writeBlock}</p> : null}
      {m.isError ? (
        <p role="alert" className="mb-3 rounded-md bg-destructive-soft px-3 py-2 text-sm text-destructive">
          {describeError(m.error)}
        </p>
      ) : null}
      <fieldset className="mb-3">
        <legend className="mb-1.5 text-sm font-medium text-muted-foreground">Due</legend>
        <div className="grid grid-cols-3 gap-2">
          {DUE.map((d) => (
            <Button
              key={d.id}
              aria-pressed={due === d.id}
              variant={due === d.id ? 'primary' : 'outline'}
              className="h-12 text-sm whitespace-normal"
              onClick={() => setDue(d.id)}
            >
              {d.label}
            </Button>
          ))}
        </div>
      </fieldset>
      <div className="grid grid-cols-2 gap-2">
        {NEXT_PRESETS.map((p) => (
          <Button
            key={p.text}
            className={BIG}
            disabled={disabled}
            disabledReason={actionReason}
            disabledReasonId={presetReasonId}
            onClick={() => m.mutate(p)}
          >
            {p.text}
          </Button>
        ))}
      </div>
      {actionReason ? (
        <p id={presetReasonId} className="mt-2 text-xs text-muted-foreground">
          {actionReason}
        </p>
      ) : null}
      <form
        className="mt-3 flex gap-2"
        onSubmit={(e) => {
          e.preventDefault()
          if (text.trim()) m.mutate({ text: text.trim(), owner: 'us' })
        }}
      >
        <Input
          aria-label="Next move"
          placeholder="Or type it"
          maxLength={500}
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="h-12 flex-1 text-base"
        />
        <Button
          type="submit"
          variant="primary"
          className="h-12"
          disabled={disabled || !text.trim()}
          disabledReason={!text.trim() ? 'Write the next move first' : actionReason}
          disabledReasonId={customReasonId}
        >
          Save
        </Button>
      </form>
      {!text.trim() || actionReason ? (
        <p id={customReasonId} className="mt-2 text-xs text-muted-foreground">
          {!text.trim() ? 'Write the next move first' : actionReason}
        </p>
      ) : null}
    </Sheet>
  )
}

/** The capture grammar's `needs on-site` (riley.md §4.6) as a next move dispatch picks up. Confirmed by a second tap. */
export const NEEDS_ONSITE_TEXT = 'needs on-site'

export function NeedsOnsiteSheet({
  open,
  onOpenChange,
  target,
  writeBlock,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  target: ActionTarget
  writeBlock: string | null
}) {
  const invalidate = useInvalidateCase()
  const m = useMutation({
    networkMode: 'always',
    // Unassigned on our side: dispatch (manager) picks the person or the contractor.
    mutationFn: () =>
      setNextMove(target.caseId, {
        text: NEEDS_ONSITE_TEXT,
        owner_kind: 'us',
        owner_user_id: null,
        due_at: nextWorkingDay(new Date(), 9).toISOString(),
      }),
    onSuccess: () => {
      toast.success(`${target.number} needs on-site. Dispatch opens the booking from it.`)
      invalidate(target.caseId)
      onOpenChange(false)
    },
  })
  const actionReason = writeBlockReason(writeBlock) ?? (m.isPending ? 'Saving…' : undefined)
  const actionReasonId = React.useId()
  return (
    <Sheet open={open} onOpenChange={onOpenChange} title="Needs on-site" description={`${target.number} · ${target.title}`}>
      <p className="mb-3 text-base">
        Sets the next move to <b>needs on-site</b>, due the next work day, for dispatch to book: the booking drawer opens with this as the
        job. Nothing is sent to the customer.
      </p>
      {writeBlock ? <p className="mb-3 rounded-md bg-warning-soft px-3 py-2 text-sm text-warning">{writeBlock}</p> : null}
      {m.isError ? (
        <p role="alert" className="mb-3 rounded-md bg-destructive-soft px-3 py-2 text-sm text-destructive">
          {describeError(m.error)}
        </p>
      ) : null}
      <div className="grid grid-cols-2 gap-2">
        <Button className={BIG} variant="ghost" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button
          className={BIG}
          variant="primary"
          disabled={Boolean(writeBlock) || m.isPending}
          disabledReason={actionReason}
          disabledReasonId={actionReasonId}
          onClick={() => m.mutate()}
        >
          Mark needs on-site
        </Button>
      </div>
      {actionReason ? (
        <p id={actionReasonId} className="mt-2 text-xs text-muted-foreground">
          {actionReason}
        </p>
      ) : null}
    </Sheet>
  )
}

const phoneToday = () => {
  const now = new Date()
  return new Date(now.getTime() - now.getTimezoneOffset() * 60_000).toISOString().slice(0, 10)
}

const optional = (value: string) => value.trim() || null

export function PartsSwappedSheet({
  open,
  onOpenChange,
  target,
  writeBlock,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  target: ActionTarget
  writeBlock: string | null
}) {
  const invalidate = useInvalidateCase()
  const queryClient = useQueryClient()
  const [itemKey, setItemKey] = React.useState(() => `phone:${uuid()}`)
  const [action, setAction] = React.useState<PartsSwappedIn['action']>('replaced')
  const [removedName, setRemovedName] = React.useState('')
  const [removedSerial, setRemovedSerial] = React.useState('')
  const [installedName, setInstalledName] = React.useState('')
  const [installedKind, setInstalledKind] = React.useState('')
  const [installedSerial, setInstalledSerial] = React.useState('')
  const [firmware, setFirmware] = React.useState('')
  const [warrantyEnd, setWarrantyEnd] = React.useState('')
  const [occurredOn, setOccurredOn] = React.useState(phoneToday)
  const [reason, setReason] = React.useState('')
  const [rma, setRma] = React.useState(false)
  const [hardwareUnitId, setHardwareUnitId] = React.useState<string | null>(null)
  const [chosenWo, setChosenWo] = React.useState<string | null>(null)
  const wos = useQuery({ ...openWorkOrdersQuery(target.caseId), enabled: open })
  const hardware = useQuery({ ...caseHardwareQuery(target.caseId), enabled: open })
  const choices = wos.data?.data.items ?? []
  // One open visit: it is the one. Several: the tech picks; nothing is guessed.
  const workOrderId = choices.length === 1 ? (choices[0]?.id ?? null) : choices.some((w) => w.id === chosenWo) ? chosenWo : null
  const receivedUnits = React.useMemo(
    () =>
      (hardware.data?.data.orders ?? []).flatMap((order) =>
        (order.units ?? []).map((unit) => ({
          ...unit,
          productName: order.lines.find((line) => line.id === unit.order_line_id)?.product_name ?? unit.model ?? 'Hardware unit',
        })),
      ),
    [hardware.data?.data.orders],
  )
  const installChoices = receivedUnits.filter((unit) => !unit.installed_wo_id || unit.installed_wo_id === workOrderId)
  const woBlock = wos.isError
    ? `Work orders are unreachable: ${describeError(wos.error)} Nothing was saved.`
    : wos.isSuccess && choices.length === 0
      ? 'This case has no open work order to record parts on.'
      : null
  React.useEffect(() => {
    if (open) {
      setChosenWo(null)
      setItemKey(`phone:${uuid()}`)
      setAction('replaced')
      setRemovedName('')
      setRemovedSerial('')
      setInstalledName('')
      setInstalledKind('')
      setInstalledSerial('')
      setFirmware('')
      setWarrantyEnd('')
      setOccurredOn(phoneToday())
      setReason('')
      setRma(false)
      setHardwareUnitId(null)
    }
  }, [open])
  const m = useMutation({
    networkMode: 'always',
    mutationFn: async (body: PartsSwappedIn) => {
      if (!workOrderId) throw new Error('Choose the work order these parts belong to.')
      if (body.hardware_unit_id) await assignHardwareUnit(body.hardware_unit_id, workOrderId)
      return savePartsSwapped(workOrderId, body)
    },
    onSuccess: () => {
      toast.success(`Parts swapped saved on ${target.number}`, {
        description: 'The Conduit close-out stays human-reviewed.',
      })
      invalidate(target.caseId)
      void queryClient.invalidateQueries({ queryKey: ['procurement', 'case', target.caseId] })
      void queryClient.invalidateQueries({ queryKey: ['work-order', workOrderId] })
      void queryClient.invalidateQueries({ queryKey: ['billing'] })
      void queryClient.invalidateQueries({ queryKey: ['ledger'] })
      onOpenChange(false)
    },
  })
  const valid =
    Boolean(workOrderId) &&
    Boolean(occurredOn) &&
    (action === 'removed'
      ? Boolean(removedName.trim())
      : action === 'added'
        ? Boolean(installedName.trim())
        : Boolean(removedName.trim() && installedName.trim()))
  return (
    <Sheet open={open} onOpenChange={onOpenChange} title="Parts swapped" description={`${target.number} · ${target.title}`}>
      {writeBlock ? <p className="mb-3 rounded-md bg-warning-soft px-3 py-2 text-sm text-warning">{writeBlock}</p> : null}
      {woBlock ? <p className="mb-3 rounded-md bg-warning-soft px-3 py-2 text-sm text-warning">{woBlock}</p> : null}
      {m.isError ? (
        <p role="alert" className="mb-3 rounded-md bg-destructive-soft px-3 py-2 text-sm text-destructive">
          {describeError(m.error)}
        </p>
      ) : null}
      {wos.isPending && open ? <p className="mb-3 text-sm text-muted-foreground">Finding the open work order...</p> : null}
      {choices.length === 1 ? (
        <p className="mb-3 text-sm text-muted-foreground">On {choices[0]?.display_number ?? 'the open work order'}</p>
      ) : null}
      {choices.length > 1 ? (
        <fieldset className="mb-3 grid gap-2">
          <legend className="mb-1 text-sm font-medium">Which work order?</legend>
          {choices.map((w) => (
            <Button
              key={w.id}
              type="button"
              variant={workOrderId === w.id ? 'primary' : 'outline'}
              className="h-11 justify-start"
              aria-pressed={workOrderId === w.id}
              onClick={() => setChosenWo(w.id)}
            >
              {`${w.display_number ?? 'Work order'} · ${w.status.replace('_', ' ')}`}
            </Button>
          ))}
        </fieldset>
      ) : null}
      {hardware.isError ? (
        <p className="mb-3 rounded-md bg-warning-soft px-3 py-2 text-sm text-warning">
          Received hardware is unreachable: {describeError(hardware.error)} You can still type the part below.
        </p>
      ) : null}
      <form
        className="space-y-3"
        onSubmit={(event) => {
          event.preventDefault()
          if (!valid) return
          m.mutate({
            item_key: itemKey,
            action,
            hardware_unit_id: action === 'removed' ? null : hardwareUnitId,
            replaces_component_id: null,
            removed_name: action === 'added' ? null : optional(removedName),
            removed_serial_number: action === 'added' ? null : optional(removedSerial),
            installed_name: action === 'removed' ? null : optional(installedName),
            installed_kind: action === 'removed' ? null : optional(installedKind),
            installed_serial_number: action === 'removed' ? null : optional(installedSerial),
            firmware_version: action === 'removed' ? null : optional(firmware),
            warranty_start: null,
            warranty_end: action === 'removed' ? null : warrantyEnd || null,
            occurred_on: occurredOn,
            reason: optional(reason),
            rma_required: action === 'replaced' && rma,
          })
        }}
      >
        {action !== 'removed' && workOrderId && installChoices.length ? (
          <fieldset className="grid gap-2">
            <legend className="mb-1 text-sm font-medium">Install a received unit (optional)</legend>
            {installChoices.map((unit) => (
              <Button
                key={unit.id}
                type="button"
                variant={hardwareUnitId === unit.id ? 'primary' : 'outline'}
                className="h-11 justify-start"
                aria-pressed={hardwareUnitId === unit.id}
                onClick={() => {
                  const next = hardwareUnitId === unit.id ? null : unit.id
                  setHardwareUnitId(next)
                  if (next) {
                    setInstalledName(unit.productName)
                    setInstalledSerial(unit.serial ?? '')
                  }
                }}
              >
                {[unit.productName, unit.serial].filter(Boolean).join(' · ')}
                {unit.installed_wo_id === workOrderId ? ' · already on this WO' : ''}
              </Button>
            ))}
          </fieldset>
        ) : null}
        <fieldset className="grid grid-cols-3 gap-2">
          <legend className="mb-1 text-sm font-medium">Change</legend>
          {(['added', 'replaced', 'removed'] as const).map((value) => (
            <Button
              key={value}
              type="button"
              variant={action === value ? 'primary' : 'outline'}
              className="h-11 capitalize"
              onClick={() => setAction(value)}
            >
              {value}
            </Button>
          ))}
        </fieldset>
        {action !== 'added' ? (
          <div className="grid grid-cols-2 gap-2">
            <Input
              aria-label="What was removed"
              placeholder="What was removed"
              value={removedName}
              onChange={(event) => setRemovedName(event.target.value)}
            />
            <Input
              aria-label="Removed serial if known"
              placeholder="Serial if known"
              value={removedSerial}
              onChange={(event) => setRemovedSerial(event.target.value)}
            />
          </div>
        ) : null}
        {action !== 'removed' ? (
          <>
            <Input
              aria-label="What was installed"
              placeholder="What was installed"
              value={installedName}
              onChange={(event) => setInstalledName(event.target.value)}
            />
            <div className="grid grid-cols-2 gap-2">
              <Input
                aria-label="Installed type"
                placeholder="Type"
                value={installedKind}
                onChange={(event) => setInstalledKind(event.target.value)}
              />
              <Input
                aria-label="Installed serial"
                placeholder="New serial"
                value={installedSerial}
                onChange={(event) => setInstalledSerial(event.target.value)}
              />
              <Input aria-label="Firmware" placeholder="Firmware" value={firmware} onChange={(event) => setFirmware(event.target.value)} />
              <Input aria-label="Warranty ends" type="date" value={warrantyEnd} onChange={(event) => setWarrantyEnd(event.target.value)} />
            </div>
          </>
        ) : null}
        <Input
          aria-label={action === 'removed' ? 'Removal date' : 'Install date'}
          type="date"
          value={occurredOn}
          onChange={(event) => setOccurredOn(event.target.value)}
        />
        <Textarea
          aria-label="Reason or note"
          rows={2}
          placeholder="Reason or note (optional)"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        {action === 'replaced' ? (
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={rma} onCheckedChange={(value) => setRma(value === true)} /> Open an RMA obligation
          </label>
        ) : null}
        <Button
          type="submit"
          variant="primary"
          className={`${BIG} w-full`}
          disabled={!valid || Boolean(writeBlock) || Boolean(woBlock) || m.isPending}
          disabledReason={
            m.isPending
              ? 'Saving…'
              : (writeBlockReason(writeBlock) ??
                woBlock ??
                (!workOrderId
                  ? 'Pick a work order first'
                  : !occurredOn
                    ? 'Choose the change date'
                    : action === 'removed'
                      ? 'Name the removed part'
                      : action === 'added'
                        ? 'Name the installed part'
                        : 'Name the removed and installed parts'))
          }
        >
          Save parts swapped
        </Button>
      </form>
    </Sheet>
  )
}

export function PhotoSheet({
  open,
  onOpenChange,
  target,
  writeBlock,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  target: ActionTarget
  writeBlock: string | null
}) {
  const invalidate = useInvalidateCase()
  const queryClient = useQueryClient()
  const [file, setFile] = React.useState<File | null>(null)
  const [chosenWo, setChosenWo] = React.useState<string | null>(null)
  const [requestId, setRequestId] = React.useState(uuid)
  const wos = useQuery({ ...photoWorkOrdersQuery(target.caseId), enabled: open })
  const choices = wos.data?.data.items ?? []
  const workOrderId = choices.length === 1 ? (choices[0]?.id ?? null) : choices.some((w) => w.id === chosenWo) ? chosenWo : null
  const selected = choices.find((wo) => wo.id === workOrderId)
  React.useEffect(() => {
    if (open) {
      setFile(null)
      setChosenWo(null)
      setRequestId(uuid())
    }
  }, [open])
  const upload = useMutation({
    networkMode: 'always',
    mutationFn: async () => {
      if (!file || !workOrderId) throw new Error('Choose a photo and the work order it belongs to.')
      const photo = await uploadWorkOrderPhoto(target.caseId, workOrderId, file, requestId)
      if (!target.ticket) return { photo, noteError: 'This case has no live Desk ticket, so no private note was posted.' }
      try {
        await postPhotoNote(target.caseId, {
          body_text: `Photo "${photo.filename}" stored on ${selected?.display_number ?? 'the work order'} in Patchbay: ${photo.app_url}`,
          desk_ticket_id: target.ticket.desk_ticket_id,
          idempotency_key: uuid(),
        })
        return { photo, noteError: null }
      } catch (error) {
        return { photo, noteError: describeError(error) }
      }
    },
    onSuccess: ({ photo, noteError }) => {
      invalidate(target.caseId)
      void queryClient.invalidateQueries({ queryKey: ['work-order', workOrderId] })
      void queryClient.invalidateQueries({ queryKey: ['case', target.caseId, 'work-orders'] })
      if (noteError) {
        toast.warning(`Photo saved on ${selected?.display_number ?? 'the work order'}`, {
          description: `The private Desk note was not posted: ${noteError}`,
        })
      } else {
        toast.success(`Photo saved on ${selected?.display_number ?? 'the work order'}`, {
          description: 'A private Desk note links to the photo.',
        })
      }
      if (!photo.replayed) setRequestId(uuid())
      onOpenChange(false)
    },
  })
  const woBlock = wos.isError
    ? `Work orders are unreachable: ${describeError(wos.error)} Nothing was saved.`
    : wos.isSuccess && choices.length === 0
      ? 'This case has no work order that can accept a photo.'
      : null
  return (
    <Sheet open={open} onOpenChange={onOpenChange} title="Take a photo" description={`${target.number} · private work-order file`}>
      <p className="mb-3 text-sm text-muted-foreground">
        The photo is stored privately in Patchbay. Desk send attachments stay off until probe P3 passes.
      </p>
      {writeBlock ? <p className="mb-3 rounded-md bg-warning-soft px-3 py-2 text-sm text-warning">{writeBlock}</p> : null}
      {woBlock ? <p className="mb-3 rounded-md bg-warning-soft px-3 py-2 text-sm text-warning">{woBlock}</p> : null}
      {wos.isPending ? <p className="mb-3 text-sm text-muted-foreground">Finding the open work order…</p> : null}
      {choices.length > 1 ? (
        <fieldset className="mb-3 grid gap-2">
          <legend className="mb-1 text-sm font-medium">Which work order?</legend>
          {choices.map((wo) => (
            <Button
              key={wo.id}
              type="button"
              variant={chosenWo === wo.id ? 'primary' : 'outline'}
              className="h-11 justify-start"
              aria-pressed={chosenWo === wo.id}
              onClick={() => setChosenWo(wo.id)}
            >
              {wo.display_number ?? 'Work order'} · {wo.status.replaceAll('_', ' ')}
            </Button>
          ))}
        </fieldset>
      ) : null}
      {choices.length === 1 ? (
        <p className="mb-3 text-sm text-muted-foreground">Save on {choices[0]?.display_number ?? 'the open work order'}.</p>
      ) : null}
      <input
        aria-label="Take or choose a photo"
        type="file"
        accept="image/*,.heic,.heif"
        capture="environment"
        onChange={(event) => setFile(event.currentTarget.files?.[0] ?? null)}
        className="mb-3 block w-full text-sm file:mr-3 file:h-11 file:cursor-pointer file:rounded-md file:border file:border-border-strong file:bg-card file:px-3"
      />
      {upload.isError ? (
        <p role="alert" className="mb-3 rounded-md bg-destructive-soft px-3 py-2 text-sm text-destructive">
          Photo was not saved: {describeError(upload.error)}
        </p>
      ) : null}
      <Button
        type="button"
        variant="primary"
        className={`${BIG} w-full`}
        disabled={!file || !workOrderId || Boolean(writeBlock) || Boolean(woBlock)}
        disabledReason={writeBlockReason(writeBlock) ?? woBlock ?? (!file ? 'Choose a photo first' : 'Pick a work order first')}
        pending={upload.isPending}
        onClick={() => upload.mutate()}
      >
        <Camera className="size-5" /> Save photo
      </Button>
    </Sheet>
  )
}

export type BarAction = 'time' | 'next' | 'onsite'

/** The sticky bottom bar: three big buttons and the camera. Disabled buttons say why, in text. */
export function ActionBar({
  target,
  writeBlock,
  onAction,
}: {
  target: ActionTarget | null
  writeBlock: string | null
  onAction: (a: BarAction) => void
}) {
  const [partsOpen, setPartsOpen] = React.useState(false)
  const [photoOpen, setPhotoOpen] = React.useState(false)
  const none = !target
  const off = none || Boolean(writeBlock)
  const reason = none ? 'Pick a case first' : writeBlockReason(writeBlock)
  const reasonId = React.useId()
  return (
    <div className="shrink-0 border-t border-border bg-background px-3 pt-2 pb-3" data-testid="action-bar">
      {target ? (
        <p className="mb-1.5 truncate text-xs text-muted-foreground">
          For <b className="text-foreground">{target.number}</b> · {target.title}
        </p>
      ) : null}
      {reason ? (
        <p id={reasonId} className="mb-1.5 text-xs text-warning" role="status">
          {reason}
        </p>
      ) : null}
      <div className="grid grid-cols-[repeat(4,minmax(0,1fr))_auto] gap-2">
        <Button
          variant="primary"
          className="h-16 min-w-0 flex-col gap-1 px-1 text-sm leading-tight whitespace-normal"
          disabled={off}
          disabledReason={reason}
          disabledReasonId={reasonId}
          onClick={() => onAction('time')}
        >
          <Clock className="size-5" /> Log time
        </Button>
        <Button
          className="h-16 min-w-0 flex-col gap-1 px-1 text-sm leading-tight whitespace-normal"
          disabled={off}
          disabledReason={reason}
          disabledReasonId={reasonId}
          onClick={() => onAction('next')}
        >
          <Navigation className="size-5" /> Next move
        </Button>
        <Button
          className="h-16 min-w-0 flex-col gap-1 px-1 text-sm leading-tight whitespace-normal"
          disabled={off}
          disabledReason={reason}
          disabledReasonId={reasonId}
          onClick={() => onAction('onsite')}
        >
          <Wrench className="size-5" /> Needs on-site
        </Button>
        <Button
          className="h-16 min-w-0 flex-col gap-1 px-1 text-sm leading-tight whitespace-normal"
          disabled={off}
          disabledReason={reason}
          disabledReasonId={reasonId}
          onClick={() => setPartsOpen(true)}
        >
          <PackageCheck className="size-5" /> Parts swapped
        </Button>
        <Button
          className="h-16 w-14"
          aria-label="Take a photo"
          disabled={off}
          disabledReason={reason}
          disabledReasonId={reasonId}
          onClick={() => setPhotoOpen(true)}
        >
          <Camera className="size-6" />
        </Button>
      </div>
      {target ? <PartsSwappedSheet open={partsOpen} onOpenChange={setPartsOpen} target={target} writeBlock={writeBlock} /> : null}
      {target ? <PhotoSheet open={photoOpen} onOpenChange={setPhotoOpen} target={target} writeBlock={writeBlock} /> : null}
    </div>
  )
}
